import { Card, CardContent, Typography, Box, Skeleton, Chip } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { DELIVERY_STATUS_COLORS, formatStatusLabel, formatNumber } from "../constants";

interface DeliveryStatsChartProps {
  data: any;
  loading: boolean;
}

const DeliveryStatsChart = ({ data, loading }: DeliveryStatsChartProps) => {
  if (loading) {
    return (
      <Card sx={{ height: "100%", borderRadius: 2, boxShadow: "0 1px 3px 0 rgba(0,0,0,.1)" }}>
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton width="40%" height={24} />
          <Skeleton variant="circular" width={200} height={200} sx={{ mt: 3, mx: "auto" }} />
        </CardContent>
      </Card>
    );
  }

  const chartData = (data?.deliveryBreakdown || []).map((item: any) => ({
    name: formatStatusLabel(item.status),
    value: item.count,
    color: DELIVERY_STATUS_COLORS[item.status] || "#94a3b8",
  }));

  return (
    <Card sx={{ height: "100%", borderRadius: 2, boxShadow: "0 1px 3px 0 rgba(0,0,0,.1)" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Delivery Performance
          </Typography>
          {/*
            * "N Failed" was wrong, and visibly so: it sat beside a pie showing
            * 14 failed plus 41 delivery_failed -- 55 items -- while the chip
            * read 30. Two different numbers for the same apparent thing, side
            * by side.
            *
            * They measure different things. The pie counts order items by
            * delivery status. This count comes from the faileddeliveries
            * collection, which is the RETRY QUEUE -- one row per item awaiting
            * another attempt, so an item that failed and was never queued, or
            * was queued and resolved, is not in it.
            *
            * Labelled for what it is. The number was never wrong; the word was.
            */}
          {data?.failedDeliveries > 0 && (
            <Chip
              label={`${data.failedDeliveries} awaiting retry`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: 11, height: 24 }}
            />
          )}
        </Box>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="45%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
              >
                {chartData.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: any, _name: any, props: any) => [
                  formatNumber(value),
                  props.payload.name,
                ]}
                contentStyle={{ borderRadius: 8, fontSize: 13 }}
              />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                iconSize={10}
                formatter={(value: string) => <span style={{ fontSize: 12, color: "#555" }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography color="text.secondary">No delivery data</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DeliveryStatsChart;
